import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { comics } from '@/mocks/comics';

const siteUrl = 'https://www.hanman.com';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'chapters' | 'info'>('chapters');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const comicId = Number(id);
  const comic = comics.find((c) => c.id === comicId);

  if (!comic) {
    return (
      <>
        <SEOHead title="漫画未找到 - 韩漫网" description="抱歉，您要找的漫画不存在。" keywords="韩漫网" />
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-4">抱歉，您要找的漫画不存在或已下架。</p>
            <Link
              to="/"
              className="inline-flex px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.categorySlug === comic.categorySlug && c.id !== comic.id)
    .slice(0, 4);

  const pageTitle = `${comic.title} - ${comic.author} - 韩漫网`;
  const pageDesc = `${comic.title}${comic.description}作者：${comic.author}，状态：${comic.status}，评分${comic.rating}分。韩漫网提供${comic.title}韩国漫画免费在线阅读。`;
  const pageKeywords = `韩漫网,${comic.title},${comic.author},韩国漫画,${comic.category},免费漫画,在线阅读`;
  const ogImage = comic.cover;

  const breadcrumbItems = [
    { label: '分类', href: '/category/all' },
    { label: comic.category, href: `/category/${comic.categorySlug}` },
    { label: comic.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩国漫画`,
    description: comic.description,
    image: comic.cover,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: '2024-01-01',
    dateModified: comic.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: '韩漫网',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: '分类', item: `${siteUrl}/category/all` },
      { '@type': 'ListItem', position: 3, name: comic.category, item: `${siteUrl}/category/${comic.categorySlug}` },
      { '@type': 'ListItem', position: 4, name: comic.title },
    ],
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={pageKeywords}
        canonicalUrl={`${siteUrl}/comic/${comic.id}`}
        ogImage={ogImage}
        ogType="article"
        schemaData={{ ...articleSchema, ...breadcrumbSchema }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <article className="flex flex-col lg:flex-row gap-8 md:gap-10 mb-12">
            <div className="flex-shrink-0">
              <div className="w-full sm:w-[280px] aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
                <img
                  src={comic.cover}
                  alt={`${comic.title} - ${comic.author} - 韩国漫画`}
                  title={`${comic.title} - 免费韩国漫画在线阅读`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                {comic.title}
              </h1>
              <p className="text-sm text-foreground-500 mb-4">作者：{comic.author}</p>

              <div className="flex flex-wrap items-center gap-3 mb-5">
                <span className="px-3 py-1 rounded-full text-sm font-semibold bg-accent-100 text-accent-900">
                  {comic.rating} 分
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  comic.status === '连载中'
                    ? 'bg-accent-100 text-accent-900'
                    : 'bg-secondary-100 text-secondary-900'
                }`}>
                  {comic.status}
                </span>
                <span className="px-3 py-1 rounded-full text-sm bg-background-100 text-foreground-600">
                  {comic.category}
                </span>
                <span className="text-sm text-foreground-400">
                  <i className="ri-eye-line mr-1"></i>
                  {comic.views > 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views} 阅读
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mb-5">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 text-xs rounded-full bg-background-100 text-foreground-600 border border-background-200/70"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <p className="text-sm text-foreground-600 leading-relaxed mb-6">{comic.description}</p>

              <button
                onClick={() => {
                  const chaptersEl = document.getElementById('chapters-section');
                  if (chaptersEl) {
                    chaptersEl.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-book-open-line"></i>
                开始阅读
              </button>
            </div>
          </article>

          <div id="chapters-section" className="mb-16">
            <div className="flex items-center border-b border-background-200/70 mb-6">
              <button
                onClick={() => setActiveTab('chapters')}
                className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'chapters'
                    ? 'border-primary-500 text-primary-500'
                    : 'border-transparent text-foreground-500 hover:text-foreground-700'
                }`}
              >
                章节列表 ({comic.chapters.length})
              </button>
              <button
                onClick={() => setActiveTab('info')}
                className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'info'
                    ? 'border-primary-500 text-primary-500'
                    : 'border-transparent text-foreground-500 hover:text-foreground-700'
                }`}
              >
                作品信息
              </button>
            </div>

            {activeTab === 'chapters' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {comic.chapters.map((chapter) => (
                  <button
                    key={chapter.id}
                    className="flex items-center justify-between p-3.5 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50 transition-colors cursor-pointer text-left group"
                    onClick={() => {
                      alert(`正在打开：${comic.title} - ${chapter.title}`);
                    }}
                  >
                    <div className="min-w-0">
                      <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors block truncate">
                        {chapter.title}
                      </span>
                      <span className="text-xs text-foreground-400 mt-0.5 block">{chapter.updatedAt}</span>
                    </div>
                    <i className="ri-arrow-right-s-line text-foreground-400 group-hover:text-primary-500 transition-colors flex-shrink-0"></i>
                  </button>
                ))}
              </div>
            )}

            {activeTab === 'info' && (
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-xs text-foreground-400 mb-1">作品名称</dt>
                    <dd className="text-sm font-medium text-foreground-800">{comic.title}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-foreground-400 mb-1">作者</dt>
                    <dd className="text-sm font-medium text-foreground-800">{comic.author}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-foreground-400 mb-1">分类</dt>
                    <dd className="text-sm font-medium text-foreground-800">{comic.category}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-foreground-400 mb-1">状态</dt>
                    <dd className="text-sm font-medium text-foreground-800">{comic.status}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-foreground-400 mb-1">评分</dt>
                    <dd className="text-sm font-medium text-foreground-800">{comic.rating} / 10</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-foreground-400 mb-1">最后更新</dt>
                    <dd className="text-sm font-medium text-foreground-800">{comic.updatedAt}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-foreground-400 mb-1">总章节</dt>
                    <dd className="text-sm font-medium text-foreground-800">{comic.chapters.length} 话</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-foreground-400 mb-1">总阅读量</dt>
                    <dd className="text-sm font-medium text-foreground-800">
                      {comic.views > 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views}
                    </dd>
                  </div>
                </dl>
              </div>
            )}
          </div>

          {relatedComics.length > 0 && (
            <section className="pb-16">
              <h2 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}